import { FC } from 'react';

import { Formik } from 'formik';
import { isStrictEqual } from 'remeda';
import { BaseCheckbox, BaseFieldArray, BaseInput, FormBodyContainer } from 'wbs-components-lib';

import { Box, Typography } from '@mui/material';

import { AvailableTickets } from '@shared/components';
import { getRewardItem, getRewardTypeSelectItems } from '@shared/utils';

import { ReferralValidationFormProps } from './types';
import { referralValidationSchema } from './validationSchema';

import styles from './ReferralValidationForm.module.css';

const { rewardsContainer } = styles;

export const ReferralValidationForm: FC<ReferralValidationFormProps> = ({
  initialValues,
  onSubmit,
  isSubmitting = false,
  submitButtonCaption = 'Update',
}) => {
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={referralValidationSchema}
      enableReinitialize
    >
      {({ submitForm, values, handleChange, handleBlur, errors, setFieldValue, touched }) => {
        const {
          requiredTasksForValidate: requiredTasksValue,
          inviterRewards: inviterRewardsValue,
          referralRewards: referralRewardsValue,
          isActive: isReferralRewardsEnabledValue,
        } = values;

        const { requiredTasksForValidate: requiredTasksError } = errors;

        const { requiredTasksForValidate: isRequiredTasksForReferralValidateTouched } = touched;

        const isRequiredTasksError = isRequiredTasksForReferralValidateTouched && !!requiredTasksError;

        const onValidationSettingsSubmit = async () => {
          await submitForm();
        };

        const isSubmitButtonDisabled = isStrictEqual(values, initialValues);

        return (
          <FormBodyContainer
            isSubmitting={isSubmitting}
            onSubmit={onValidationSettingsSubmit}
            submitButtonCaption={submitButtonCaption}
            isSubmitButtonDisabled={isSubmitButtonDisabled}
          >
            <BaseCheckbox
              id="isActive"
              name="isActive"
              title="Active"
              checked={isReferralRewardsEnabledValue}
              onChange={handleChange}
            />
            <BaseInput
              type="number"
              helperText={isRequiredTasksError && requiredTasksError}
              id="requiredTasksForValidate"
              name="requiredTasksForValidate"
              label="Number of tasks required for referral validation (Max: 5)"
              value={requiredTasksValue}
              onBlur={handleBlur}
              onChange={handleChange}
              error={isRequiredTasksError}
            />

            <Box className={rewardsContainer}>
              <Typography fontSize="16px" fontWeight={600}>
                Rewards for the inviter
              </Typography>

              <AvailableTickets rewardsDataValue={referralRewardsValue} />

              <BaseFieldArray
                fieldArrayName="referralRewards"
                items={referralRewardsValue}
                getBaseFieldValue={getRewardItem}
                selectSettings={{
                  selectFields: { type: getRewardTypeSelectItems() },
                }}
                touched={touched}
                errors={errors}
                label="Reward"
                handleBlur={handleBlur}
                handleChange={handleChange}
                setFieldValue={setFieldValue}
              />

              <Typography fontSize="16px" fontWeight={600}>
                Rewards for the referral
              </Typography>

              <AvailableTickets rewardsDataValue={inviterRewardsValue} />

              <BaseFieldArray
                fieldArrayName="inviterRewards"
                items={inviterRewardsValue}
                getBaseFieldValue={getRewardItem}
                selectSettings={{
                  selectFields: { type: getRewardTypeSelectItems() },
                }}
                touched={touched}
                errors={errors}
                label="Reward"
                handleBlur={handleBlur}
                handleChange={handleChange}
                setFieldValue={setFieldValue}
              />
            </Box>
          </FormBodyContainer>
        );
      }}
    </Formik>
  );
};
